import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  LocalHospital as DoctorIcon,
  Description as ReportIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  HealthAndSafety as HealthIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  MedicalServices as MedicalIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  Assessment as AssessmentIcon,
  EventBusy as LeaveIcon,
  MonitorHeart as OperationsIcon,
  Tune as ConfigurationIcon,
  Science as LabIcon,
  Biotech as InvestigationIcon,
  Bloodtype as BloodTestIcon,
  BusinessCenter as OfficeIcon,
  Shield as InsuranceIcon,
  Timeline as TimelineIcon,
  FolderShared as DocumentsIcon,
  CorporateFare as StaffIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import { logout } from '../store/slices/authSlice';
import { fetchDoctorDashboard } from '../store/slices/dashboardSlice';

const DRAWER_WIDTH = 260;

interface MenuItemType {
  title: string;
  icon: React.ReactElement;
  path: string;
  badge?: number;
}

const DashboardLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (user?.role === 'GOVERNMENT_DOCTOR') {
      dispatch(fetchDoctorDashboard() as any);
    }
  }, [dispatch, user?.role]);

  const getMenuItems = (): MenuItemType[] => {
    const role = user?.role;

    if (role === 'CIVILHEALTH_COORDINATOR' || role === 'SYSTEM_ADMIN') {
      return [
        { title: 'Dashboard', icon: <DashboardIcon />, path: '/admin-dashboard' },
        { title: 'Operations Center', icon: <OperationsIcon />, path: '/admin/operations' },
        { title: 'Doctor Management', icon: <DoctorIcon />, path: '/admin/doctors' },
        { title: 'Officer Management', icon: <PersonIcon />, path: '/admin/officers' },
        { title: 'Appointment Control', icon: <CalendarIcon />, path: '/admin/appointments' },
        { title: 'Case Management', icon: <AssignmentIcon />, path: '/admin/cases' },
        { title: 'Medical Records', icon: <MedicalIcon />, path: '/admin/medical-records' },
        // Phase-2 modules (additive)
        { title: 'Admission Approvals', icon: <OperationsIcon />, path: '/admin/admissions' },
        { title: 'Investigation Approvals', icon: <InvestigationIcon />, path: '/admin/investigations' },
        { title: 'Insurance Verification', icon: <InsuranceIcon />, path: '/admin/insurance' },
        { title: 'Laboratories', icon: <LabIcon />, path: '/admin/laboratories' },
        { title: 'Analytics', icon: <AssessmentIcon />, path: '/admin/analytics' },
        { title: 'Notifications', icon: <NotificationsIcon />, path: '/admin/notifications' },
        { title: 'Audit Logs', icon: <ReportIcon />, path: '/admin/audit-logs' },
        { title: 'Configuration', icon: <ConfigurationIcon />, path: '/admin/configuration' },
        { title: 'Settings', icon: <SettingsIcon />, path: '/settings' },
      ];
    }

    if (role === 'GOVERNMENT_DOCTOR') {
      return [
        { title: 'Dashboard', icon: <DashboardIcon />, path: '/doctor-dashboard' },
        { title: "Today's Schedule", icon: <ScheduleIcon />, path: '/doctor/schedule' },
        { title: 'Appointments', icon: <CalendarIcon />, path: '/doctor/appointments' },
        { title: 'Notifications', icon: <NotificationsIcon />, path: '/doctor/notifications', badge: 3 },
        { title: 'My Availability', icon: <TimeIcon />, path: '/doctor/availability' },
        { title: 'Leave Management', icon: <LeaveIcon />, path: '/doctor/leaves' },
        { title: 'Reports & Analytics', icon: <AssessmentIcon />, path: '/doctor/reports' },
        // Phase-2 (additive)
        { title: 'Prescription Uploads', icon: <ReportIcon />, path: '/prescription-documents' },
        { title: 'Profile', icon: <AccountIcon />, path: '/profile' },
        { title: 'Settings', icon: <SettingsIcon />, path: '/settings' },
      ];
    }

    if (role === 'LAB_ADMIN') {
      return [
        { title: 'Dashboard', icon: <DashboardIcon />, path: '/lab-dashboard' },
        { title: 'Investigations', icon: <InvestigationIcon />, path: '/lab/investigations' },
        { title: 'Blood Tests', icon: <BloodTestIcon />, path: '/lab/blood-tests' },
        { title: 'Test Catalog', icon: <LabIcon />, path: '/lab/catalog' },
        { title: 'Profile', icon: <AccountIcon />, path: '/profile' },
        { title: 'Settings', icon: <SettingsIcon />, path: '/settings' },
      ];
    }

    if (role === 'OFFICE_ADMIN') {
      return [
        { title: 'Dashboard', icon: <DashboardIcon />, path: '/office-dashboard' },
        { title: 'Office Management', icon: <OfficeIcon />, path: '/office-management' },
        { title: 'Officers', icon: <PersonIcon />, path: '/officers' },
        // Phase-2 officer modules (additive) — office admin acts on behalf of a chosen
        // officer via the OfficerSelector picker on each page (?officerId=N).
        { title: 'Admission Approvals', icon: <OperationsIcon />, path: '/admin/admissions' },
        { title: 'Investigation Approvals', icon: <InvestigationIcon />, path: '/admin/investigations' },
        { title: 'Insurance Verification', icon: <InsuranceIcon />, path: '/admin/insurance' },
        { title: 'Medical Reports', icon: <ReportIcon />, path: '/officer/medical-reports' },
        { title: 'Insurance', icon: <InsuranceIcon />, path: '/officer/insurance' },
        { title: 'Admissions', icon: <OperationsIcon />, path: '/officer/admissions' },
        { title: 'Investigations', icon: <InvestigationIcon />, path: '/officer/investigations' },
        { title: 'Blood Tests', icon: <BloodTestIcon />, path: '/officer/blood-tests' },
        { title: 'Documents', icon: <DocumentsIcon />, path: '/officer/documents' },
        { title: 'Prescription Uploads', icon: <ReportIcon />, path: '/prescription-documents' },
        { title: 'Health Timeline', icon: <TimelineIcon />, path: '/officer/timeline' },
        { title: 'Settings', icon: <SettingsIcon />, path: '/settings' },
      ];
    }

    if (role === 'LOKBHAVAN_STAFF') {
      return [
        { title: 'Dashboard', icon: <DashboardIcon />, path: '/staff-dashboard' },
        { title: 'Medical Reports', icon: <ReportIcon />, path: '/officer/medical-reports' },
        { title: 'Prescription Uploads', icon: <ReportIcon />, path: '/prescription-documents' },
        { title: 'Documents', icon: <DocumentsIcon />, path: '/officer/documents' },
        { title: 'Officers', icon: <PersonIcon />, path: '/officers' },
        { title: 'Settings', icon: <SettingsIcon />, path: '/settings' },
      ];
    }

    return [
      { title: 'Dashboard', icon: <DashboardIcon />, path: '/officer-dashboard' },
      { title: 'Doctors', icon: <DoctorIcon />, path: '/doctors' },
      { title: 'Appointments', icon: <CalendarIcon />, path: '/appointments', badge: 2 },
      // Phase-2 modules (additive)
      { title: 'Medical Reports', icon: <ReportIcon />, path: '/officer/medical-reports' },
      { title: 'Insurance', icon: <InsuranceIcon />, path: '/officer/insurance' },
      { title: 'Admissions', icon: <OperationsIcon />, path: '/officer/admissions' },
      { title: 'Investigations', icon: <InvestigationIcon />, path: '/officer/investigations' },
      { title: 'Blood Tests', icon: <BloodTestIcon />, path: '/officer/blood-tests' },
      { title: 'Documents', icon: <DocumentsIcon />, path: '/officer/documents' },
      { title: 'Prescription Uploads', icon: <ReportIcon />, path: '/prescription-documents' },
      { title: 'Health Timeline', icon: <TimelineIcon />, path: '/officer/timeline' },
      { title: 'Profile', icon: <AccountIcon />, path: '/profile' },
      { title: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    ];
  };

  const menuItems = getMenuItems();

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleProfileMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleNotificationOpen = (e: React.MouseEvent<HTMLElement>) => setNotificationAnchor(e.currentTarget);
  const handleNotificationClose = () => setNotificationAnchor(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) setDrawerOpen(false);
  };

  const getRoleDisplayName = () => {
    const roles: Record<string, string> = {
      CIVILHEALTH_COORDINATOR: 'Coordinator',
      SYSTEM_ADMIN: 'Admin',
      GOVERNMENT_DOCTOR: 'Doctor',
      IAS_OFFICER: 'IAS Officer',
      IPS_OFFICER: 'IPS Officer',
      IFS_OFFICER: 'IFS Officer',
      IRS_OFFICER: 'IRS Officer',
      JUDGE: 'Judge',
      MINISTER: 'Minister',
      OSD: 'OSD',
      SENIOR_GOVERNMENT_OFFICER: 'Senior Officer',
      LOKBHAVAN_STAFF: 'LokBhavan Staff',
      OFFICE_ADMIN: 'Office Admin',
      LAB_ADMIN: 'Lab Admin',
    };
    return roles[user?.role || ''] || 'User';
  };

  const getRoleColor = () => {
    const colors: Record<string, string> = {
      CIVILHEALTH_COORDINATOR: '#3B82F6',
      SYSTEM_ADMIN: '#3B82F6',
      GOVERNMENT_DOCTOR: '#10B981',
      IAS_OFFICER: '#F59E0B',
      IPS_OFFICER: '#EF4444',
      IFS_OFFICER: '#10B981',
      IRS_OFFICER: '#8B5CF6',
      JUDGE: '#3B82F6',
      MINISTER: '#F59E0B',
      LOKBHAVAN_STAFF: '#0891B2',
      OFFICE_ADMIN: '#7C3AED',
      LAB_ADMIN: '#0EA5E9',
    };
    return colors[user?.role || ''] || '#3B82F6';
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'transparent' }}>
      {/* Logo */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #E5E7EB' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
          }}
        >
          <HealthIcon sx={{ color: '#fff', fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1A1A2E', lineHeight: 1.2 }}>
            Civilhealth
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#F59E0B', fontWeight: 600 }}>
            Healthcare System
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#6B7280', p: 0.5 }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>

      {/* User Info */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 100%)', border: '1px solid #E5E7EB' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 44, height: 44, bgcolor: getRoleColor(), fontWeight: 700, fontSize: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
              {user?.fullName?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1A1A2E' }} noWrap>
                {user?.fullName || 'User'}
              </Typography>
              <Chip
                label={getRoleDisplayName()}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  mt: 0.5,
                  bgcolor: `${getRoleColor()}15`,
                  color: getRoleColor(),
                  border: `1px solid ${getRoleColor()}40`,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <List sx={{ px: 1.5, py: 1, flex: 1, overflow: 'auto' }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;

          return (
            <ListItemButton
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                mx: 0.5,
                my: 0.5,
                borderRadius: 2.5,
                py: 1.25,
                px: 2,
                bgcolor: isActive ? '#EFF6FF' : 'transparent',
                color: isActive ? '#2563EB' : '#374151',
                border: isActive ? '1px solid #93C5FD' : '1px solid transparent',
                '&:hover': {
                  bgcolor: 'transparent' ,
                  color: '#1A1A2E',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{ fontWeight: isActive ? 600 : 500, fontSize: '0.9rem' }}
              />
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  sx={{
                    height: 22,
                    minWidth: 22,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: 'transparent' ,
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
                  }}
                />
              )}
            </ListItemButton>
          );
        })}
      </List>

      {/* Footer */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(135deg, #FFFBEB 0%, #ECFDF5 100%)', border: '1px solid #E5E7EB' }}>
          <Typography sx={{ color: '#F59E0B', fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>PREMIUM SUPPORT</Typography>
          <Typography sx={{ color: '#1A1A2E', fontSize: '0.85rem', fontWeight: 600 }}>Contact Support</Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'transparent' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%' },
          ml: { md: drawerOpen ? `${DRAWER_WIDTH}px` : 0 },
          bgcolor: 'transparent' ,
          borderBottom: '1px solid #E5E7EB',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Toolbar sx={{ px: 3, minHeight: 64 }}>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: drawerOpen ? 'none' : 'block' }, color: '#374151' }}
          >
            <MenuIcon />
          </IconButton>

          <Typography sx={{ flex: 1, fontWeight: 700, color: '#1A1A2E', fontSize: '1.1rem' }}>
            {menuItems.find((item) => item.path === location.pathname)?.title || 'Dashboard'}
          </Typography>

          <Tooltip title="Notifications">
            <IconButton onClick={handleNotificationOpen} sx={{ mx: 1, color: '#6B7280', '&:hover': { color: '#1A1A2E' } }}>
              <Badge badgeContent={3} color="error"><NotificationsIcon /></Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title="Account">
            <IconButton edge="end" onClick={handleProfileMenuOpen} sx={{ ml: 1 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: getRoleColor(), fontWeight: 600, fontSize: '0.9rem', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                {user?.fullName?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 3,
                bgcolor: 'transparent' ,
                border: '1px solid #E5E7EB',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <Box sx={{ px: 2, py: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#1A1A2E' }}>{user?.fullName}</Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>{user?.email}</Typography>
            </Box>
            <Divider sx={{ borderColor: '#E5E7EB' }} />
            <MenuItem onClick={() => { handleNavigation('/profile'); handleProfileMenuClose(); }} sx={{ color: '#374151', borderRadius: 2, mx: 1, '&:hover': { bgcolor: 'transparent' } }}>
              <ListItemIcon><AccountIcon fontSize="small" sx={{ color: '#6B7280' }} /></ListItemIcon><ListItemText>My Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleNavigation('/settings'); handleProfileMenuClose(); }} sx={{ color: '#374151', borderRadius: 2, mx: 1, '&:hover': { bgcolor: 'transparent' } }}>
              <ListItemIcon><SettingsIcon fontSize="small" sx={{ color: '#6B7280' }} /></ListItemIcon><ListItemText>Settings</ListItemText>
            </MenuItem>
            <Divider sx={{ borderColor: '#E5E7EB' }} />
            <MenuItem onClick={handleLogout} sx={{ color: '#EF4444', borderRadius: 2, mx: 1, '&:hover': { bgcolor: 'transparent' } }}>
              <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#EF4444' }} /></ListItemIcon><ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1.5,
                width: 340,
                maxHeight: 400,
                borderRadius: 3,
                bgcolor: 'transparent' ,
                border: '1px solid #E5E7EB',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <Box sx={{ p: 2, borderBottom: '1px solid #E5E7EB' }}>
              <Typography sx={{ fontWeight: 600, color: '#1A1A2E' }}>Notifications</Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>You have 3 unread</Typography>
            </Box>
            {[
              { title: 'New appointment request', desc: 'Dr. Kumar requested schedule change', time: '5m ago' },
              { title: 'Visit report available', desc: 'Your medical report is ready', time: '1h ago' },
              { title: 'Reminder: Appointment tomorrow', desc: 'You have an appointment at 10:00 AM', time: '2h ago' },
            ].map((notif, i) => (
              <MenuItem key={i} sx={{ py: 1.5, px: 2, borderBottom: i < 2 ? '1px solid #F3F4F6' : 'none', color: '#374151', '&:hover': { bgcolor: 'transparent' } }}>
                <Box>
                  <Typography sx={{ fontWeight: 500, color: '#1A1A2E', fontSize: '0.85rem' }}>{notif.title}</Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>{notif.desc}</Typography>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.7rem', mt: 0.5 }}>{notif.time}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: 'transparent' ,
            borderRight: '1px solid #E5E7EB',
            boxShadow: '4px 0 16px rgba(0, 0, 0, 0.05)',
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          width: '100%',
          bgcolor: 'transparent' ,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default DashboardLayout;